import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Check, X, AlertTriangle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BatchRowData } from '@/hooks/useBatchJobs';
import { QRDesignSettings } from '@/stores/qrStore';

interface BatchGenerateStepProps {
  data: BatchRowData[];
  designSettings: Partial<QRDesignSettings>;
  isGenerating: boolean;
  progress: { processed: number; success: number; failed: number };
  onGenerate: () => void;
  onCancel: () => void;
}

export function BatchGenerateStep({
  data,
  designSettings,
  isGenerating,
  progress,
  onGenerate,
  onCancel,
}: BatchGenerateStepProps) {
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!isGenerating) {
      setElapsedTime(0);
      return;
    }
    
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isGenerating]);

  const progressPercent = data.length > 0 ? (progress.processed / data.length) * 100 : 0;
  const estimatedTimeRemaining = progress.processed > 0 
    ? Math.round((elapsedTime / progress.processed) * (data.length - progress.processed))
    : null;

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Summary */}
      <div className="surface-elevated rounded-xl p-6 text-center">
        <div className="text-5xl font-bold mb-2">{data.length}</div>
        <div className="text-muted-foreground">QR codes to generate</div>
      </div>

      {/* Progress Section */}
      {isGenerating ? (
        <div className="surface-elevated rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-lg font-medium">
              Generating QR codes... {progress.processed}/{data.length}
            </span>
          </div>
          
          <Progress value={progressPercent} className="h-3" />
          
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Elapsed: {formatTime(elapsedTime)}</span>
            {estimatedTimeRemaining !== null && (
              <span>Est. remaining: {formatTime(estimatedTimeRemaining)}</span>
            )}
          </div>
          
          <div className="flex items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              <span>{progress.success} successful</span>
            </div>
            {progress.failed > 0 && (
              <div className="flex items-center gap-2 text-destructive">
                <X className="h-4 w-4" />
                <span>{progress.failed} failed</span>
              </div>
            )}
          </div>
          
          <div className="text-center">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="surface-elevated rounded-xl p-6">
          {/* Pre-generation summary */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-2xl font-bold">{data.length}</div>
              <div className="text-sm text-muted-foreground">QR Codes</div>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="text-2xl font-bold">
                ~{Math.ceil(data.length * 0.5)}s
              </div>
              <div className="text-sm text-muted-foreground">Est. Time</div>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20 mb-6">
            <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Ready to generate</p>
              <p className="text-muted-foreground">
                This will create {data.length} QR codes with the selected design.
                The process may take a few minutes.
              </p>
            </div>
          </div>
          
          <Button 
            onClick={onGenerate} 
            className="w-full gradient-hero"
            size="lg"
          >
            Generate All QR Codes
          </Button>
        </div>
      )}
    </motion.div>
  );
}
